/**
 * Payment cards endpoints for Mews Booking Engine API
 *
 * Step 4.4 Option B: Payment Card Authorization
 *
 * Implements:
 * - getPaymentCards() - Fetch payment card details (authorization state)
 * - authorizePaymentCard() - Request authorization of a payment card
 * - collectBrowserInfo() - Collect BrowserInfo for authorize (Task 6)
 *
 * @see STEP_4_4_OPTION_B_TASKMASTER.md - Task 1, Task 6
 * @see payment-cards.md - Get payment cards, Authorize payment card
 */

import { getApiClient } from './client';
import type {
  ApiResponse,
  GetPaymentCardsResponse,
  AuthorizePaymentCardResponse,
  BrowserInfo,
} from './types';

/**
 * Collects BrowserInfo from the current browser for authorizePaymentCard.
 * Call this right before calling authorizePaymentCard (e.g. in Book.tsx).
 *
 * @see STEP_4_4_OPTION_B_TASKMASTER.md - Task 6
 * @see payment-cards.md - Browser info
 */
export function collectBrowserInfo(): BrowserInfo {
  return {
    ScreenWidth: Math.floor(screen.width),
    ScreenHeight: Math.floor(screen.height),
    ColorDepth: Math.floor(screen.colorDepth),
    UserAgent: navigator.userAgent,
    Language: navigator.language,
    JavaEnabled: false,
    TimeZoneOffset: -new Date().getTimezoneOffset(),
  };
}

/** Parameters for getPaymentCards() */
export interface GetPaymentCardsParams {
  /** Unique identifiers of the payment cards to fetch */
  paymentCardIds: string[];
}

/** Parameters for authorizePaymentCard() */
export interface AuthorizePaymentCardParams {
  /** Enterprise ID (use HotelId from env) */
  enterpriseId: string;
  /** Unique identifier of the payment card to authorize */
  paymentCardId: string;
  /** Browser info (screen, navigator, timezone) - collected by caller */
  browserInfo: BrowserInfo;
}

/**
 * Fetches details about the listed payment cards, in particular the Authorization state.
 *
 * Endpoint: POST /api/distributor/v1/paymentCards/getAll
 *
 * Use after createReservationGroup (with creditCardData) to check if the card is Authorizable
 * before calling authorizePaymentCard.
 *
 * @see payment-cards.md - Get payment cards
 */
export async function getPaymentCards(
  params: GetPaymentCardsParams
): Promise<ApiResponse<GetPaymentCardsResponse>> {
  if (import.meta.env.DEV) {
    console.log('[getPaymentCards] called with params:', {
      paymentCardIdsCount: params.paymentCardIds.length,
    });
  }

  const client = getApiClient();

  const requestBody = {
    Client: client.getClientIdentifier(),
    PaymentCardIds: params.paymentCardIds,
  };

  if (import.meta.env.DEV) {
    console.log('[getPaymentCards] API call – request:', {
      endpoint: '/api/distributor/v1/paymentCards/getAll',
      ...requestBody,
    });
  }

  const result = await client.post<GetPaymentCardsResponse>(
    '/api/distributor/v1/paymentCards/getAll',
    requestBody
  );

  if (import.meta.env.DEV) {
    if (result.error) {
      console.log('[getPaymentCards] API call – error response:', {
        status: result.status,
        error: result.error,
      });
    } else {
      console.log('[getPaymentCards] API call – success response:', {
        status: result.status,
        data: result.data,
      });
    }
  }

  return result;
}

/**
 * Requests authorization of the specified payment card.
 *
 * Endpoint: POST /api/distributor/v1/paymentCards/authorize
 *
 * Call after createReservationGroup when getPaymentCards returns AuthorizationState 'Authorizable'.
 * BrowserInfo must be collected in the browser (screen.*, navigator.*, Date.getTimezoneOffset()).
 *
 * Response State: Authorized (finite), Declined (finite), Canceled (finite),
 * Pending (non-finite, redirect to 3DS), Requested (non-finite, redirect to 3DS).
 *
 * @see payment-cards.md - Authorize payment card
 */
export async function authorizePaymentCard(
  params: AuthorizePaymentCardParams
): Promise<ApiResponse<AuthorizePaymentCardResponse>> {
  if (import.meta.env.DEV) {
    console.log('[authorizePaymentCard] called with params:', {
      enterpriseId: params.enterpriseId,
      paymentCardId: params.paymentCardId,
      browserInfo: params.browserInfo,
    });
  }

  const client = getApiClient();

  const requestBody = {
    Client: client.getClientIdentifier(),
    EnterpriseId: params.enterpriseId,
    PaymentCardId: params.paymentCardId,
    BrowserInfo: params.browserInfo,
  };

  if (import.meta.env.DEV) {
    console.log('[authorizePaymentCard] API call – request:', {
      endpoint: '/api/distributor/v1/paymentCards/authorize',
      ...requestBody,
    });
  }

  const result = await client.post<AuthorizePaymentCardResponse>(
    '/api/distributor/v1/paymentCards/authorize',
    requestBody
  );

  if (import.meta.env.DEV) {
    if (result.error) {
      console.log('[authorizePaymentCard] API call – error response:', {
        status: result.status,
        error: result.error,
      });
    } else {
      console.log('[authorizePaymentCard] API call – success response:', {
        status: result.status,
        data: result.data,
      });
    }
  }

  return result;
}
